import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges
} from '@angular/core';
import {
  TixGetFeesForVenueGQL,
  TixBuyerProductFragmentFragment
} from '@tix/data-access';
import { OrderTicket } from '@tix/event-buyer/services';
import { formatAsDollarFigure, dollarFigureToNumber } from '@tix/shared/utils';
import {
  Calculator,
  Discounts,
  onlineProcessingFeePreset,
  Order,
  Ticket,
  Addon
} from 'asylumtix-calculator';

@Component({
  selector: 'tix-order-calculations',
  templateUrl: './order-calculations.component.html',
  styleUrls: ['./order-calculations.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TixOrderCalculationsComponent implements OnInit, OnChanges {
  @Input() tickets: OrderTicket[] = [];
  @Input() products: (TixBuyerProductFragmentFragment & {
    selected: number;
  })[] = [];
  @Input() venueId: string;
  @Input() discount?: any;
  @Input() donationAmount = 0;

  ticketsSubtotal = 0;
  addonsSubtotal = 0;
  addonsTotal = 0;
  totalProcessingFee = 0;
  totalPlatformFee = 0;
  totalVenueFee = 0;
  feesTotal = 0;
  discountAmount = 0;
  amountToPay = 0;
  totalWithNoFees = 0;
  venuePlatformFee = 3;

  private calculatorService?: Calculator;

  constructor(private getFeesForVenueQuery: TixGetFeesForVenueGQL) {}

  ngOnInit() {
    this.initializeCalculator();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['tickets'] ||
      changes['products'] ||
      changes['discount'] ||
      changes['donationAmount']
    ) {
      this.calculateAmounts();
    }
  }

  private async initializeCalculator() {
    if (!this.venueId) return;

    const res = await this.getFeesForVenueQuery
      .fetch({
        venueId: this.venueId
      })
      .toPromise();

    this.venuePlatformFee =
      res.data.fees.filter(f => f.feeType === 'platform')[0]?.amount || 3;

    this.calculatorService = new Calculator({
      platformFee: { amount: this.venuePlatformFee },
      processingFee: onlineProcessingFeePreset,
      processingFeePrecision: 2
    });

    this.calculateAmounts();
  }

  private get orderTickets(): (Ticket & { ticketConfigId: string })[] {
    const ticketArrays = this.tickets
      .filter(ticket => ticket.ticketQty > 0)
      .map(ticket =>
        Array.from({ length: ticket.ticketQty }, () => ({
          id: `${ticket.ticketConfigId}-${Math.random()}`,
          venueFee: dollarFigureToNumber(ticket.venueFee),
          price: dollarFigureToNumber(ticket.ticketPrice),
          includeFeesInPrice: ticket.includeFees || false,
          ticketConfigId: ticket.ticketConfigId
        }))
      );

    return ticketArrays.reduce((acc, curr) => acc.concat(curr), []);
  }

  private get orderAddons(): (Addon & { productConfigId: string })[] {
    const addonArrays = this.products
      .filter(p => p.selected > 0)
      .map(product =>
        Array.from({ length: product.selected }, () => ({
          id: `${product.eventProductId}-${Math.random()}`,
          price: this.dollarFigureToNumber(product.price),
          tax: product.tax,
          productConfigId: product.eventProductId
        }))
      );

    return addonArrays.reduce((acc, curr) => acc.concat(curr), []);
  }

  private get order(): Order {
    let appliedDiscount;

    if (this.discount?.type === '$ Discount') {
      appliedDiscount = new Discounts.StaticDiscount({
        amount: this.discount.getAmount!,
        appliedTo:
          this.discount?.appliedTo === 'Ticket' ? 'ticket' : 'reservation'
      });
    } else if (this.discount?.type === '% Discount') {
      appliedDiscount = new Discounts.PercentageDiscount({
        amount: this.discount.getAmount! / 100
      });
    } else if (this.discount?.type === 'Buy X Get Y Free') {
      appliedDiscount = new Discounts.BuyXGetYFreeDiscount({
        freeAmount: this.discount.getAmount!,
        buyAmount: this.discount?.buyAmount!
      });
    } else if (this.discount?.type === 'Spend $X, Get $Y Off') {
      appliedDiscount = new Discounts.SpendXGetYOffDiscount({
        offAmount: this.discount.getAmount!,
        spendAmount: this.discount?.buyAmount!
      });
    }

    return {
      tickets: this.orderTickets,
      addons: this.orderAddons,
      discount: appliedDiscount,
      donation: this.donationAmount
        ? {
            amount: this.donationAmount
          }
        : undefined
    };
  }

  private get includeFees(): boolean {
    return this.tickets.some(ticket => ticket.includeFees);
  }

  private calculateAmounts() {
    if (!this.calculatorService) return;

    const order = this.order;

    this.ticketsSubtotal = this.calculatorService.getTicketsSubtotal(
      order.tickets
    );
    this.addonsSubtotal = this.calculatorService.getAddonsSubtotal(
      order.addons
    );
    this.addonsTotal =
      this.addonsSubtotal + this.calculatorService.getAddonsTax(order.addons);

    this.discountAmount = this.calculatorService.getTotalDiscountAmount(order);
    this.totalWithNoFees = this.ticketsSubtotal + this.addonsSubtotal;

    this.totalProcessingFee =
      this.calculatorService.getProcessingFeeForOrder(order);
    this.totalPlatformFee = this.calculatorService.getPlatformFee(order);
    this.totalVenueFee = this.calculatorService.getVenueFee(order);

    if (!this.includeFees) {
      this.feesTotal =
        this.totalProcessingFee + this.totalPlatformFee + this.totalVenueFee;
    } else {
      const addonsProcessingFee = order.addons.reduce(
        (total, addon) =>
          total + this.calculatorService!.getAddonProcessingFee(order, addon),
        0
      );
      const donationProcessingFee =
        this.calculatorService.getProcessingFeeForDonation(order);
      this.feesTotal = addonsProcessingFee + donationProcessingFee;
    }

    this.amountToPay = this.calculatorService.getTotalToPay(order) || 0;
  }

  private dollarFigureToNumber(dollarFigure?: string | number): number {
    if (typeof dollarFigure === 'number') {
      return dollarFigure;
    }
    if (!dollarFigure) return 0;
    return Number(dollarFigure.replace(/[^0-9.-]+/g, ''));
  }

  get hasItems(): boolean {
    return (
      this.tickets.some(t => t.ticketQty > 0) ||
      this.products.some(p => p.selected > 0)
    );
  }

  get totalItems(): number {
    const ticketCount = this.tickets.reduce(
      (sum, t) => sum + (t.ticketQty || 0),
      0
    );
    const productCount = this.products.reduce(
      (sum, p) => sum + (p.selected || 0),
      0
    );
    return ticketCount + productCount;
  }

  formatAsDollarFigure = formatAsDollarFigure;
}
